import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PaymentService from '../../services/PaymentService';
import {
  PageContainer as PaymentContainer,
  PaymentCard,
  PaymentBrandPanel,
  CheckoutPanel,
  Header,
  Title,
  Subtitle,
  SummaryBox,
  AmountLabel,
  Amount,
  DetailRow,
  DetailLabel,
  DetailValue,
  PrimaryButton,
  LoadingSpinner,
  StatusWrap,
  StatusIcon,
  StatusTitle,
  StatusMessage,
  AlertBox,
  SecureMark,
} from './PaymentVisuals';

const AmountDisplay = SummaryBox;
const OrderInfo = SummaryBox;
const OrderRow = DetailRow;
const OrderLabel = DetailLabel;
const OrderValue = DetailValue;
const PayButton = PrimaryButton;
const StatusContainer = StatusWrap;
const TransactionId = StatusMessage;
const CloseButton = PrimaryButton;
const ErrorMessage = AlertBox;

const Payment = () => {
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('initial'); // initial, processing, success, failed, pending
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState(null);

  // Parse payment details from URL params
  useEffect(() => {
    const amount = searchParams.get('amount');
    const orderId = searchParams.get('orderId');
    const appName = searchParams.get('appName') || 'External App';
    const callbackUrl = searchParams.get('callbackUrl');
    const description = searchParams.get('description') || 'Payment';
    const customerName = searchParams.get('customerName');
    const customerPhone = searchParams.get('customerPhone');
    const customerEmail = searchParams.get('customerEmail');

    if (!amount || !orderId) {
      setError('Invalid payment request. Missing required parameters.');
      setLoading(false);
      return;
    }

    setPaymentDetails({
      amount: parseFloat(amount),
      orderId,
      appName,
      callbackUrl,
      description,
      customerName,
      customerPhone,
      customerEmail
    });
    setLoading(false);
  }, [searchParams]);

  const initiatePayment = async () => {
    if (!paymentDetails) return;

    setPaymentStatus('processing');
    setError(null);

    try {
      // Call backend to create PhonePe payment order
      const response = await PaymentService.createPaymentOrder({
        amount: paymentDetails.amount,
        orderId: paymentDetails.orderId,
        description: paymentDetails.description,
        customerName: paymentDetails.customerName,
        customerPhone: paymentDetails.customerPhone,
        customerEmail: paymentDetails.customerEmail,
        appName: paymentDetails.appName
      });

      if (response.success && response.data.redirectUrl) {
        // Store transaction ID for status check
        setTransactionId(response.data.transactionId);
        
        // Redirect to PhonePe payment page
        window.location.href = response.data.redirectUrl;
      } else {
        throw new Error(response.message || 'Failed to initiate payment');
      }
    } catch (err) {
      setError(err.message || 'Payment initiation failed. Please try again.');
      setPaymentStatus('initial');
    }
  };

  const handlePaymentCallback = async (status, txnId) => {
    setTransactionId(txnId);
    
    if (status === 'success') {
      setPaymentStatus('success');
    } else if (status === 'failed') {
      setPaymentStatus('failed');
    } else {
      setPaymentStatus('pending');
    }
  };

  const closePaymentWindow = () => {
    // Send message to parent window if opened as popup
    if (window.opener) {
      window.opener.postMessage({
        type: 'WILLOVATE_PAYMENT_COMPLETE',
        status: paymentStatus,
        transactionId,
        orderId: paymentDetails?.orderId
      }, '*');
      window.close();
    } 
    // Redirect to callback URL if provided
    else if (paymentDetails?.callbackUrl) {
      const callbackUrl = new URL(paymentDetails.callbackUrl);
      callbackUrl.searchParams.set('status', paymentStatus);
      callbackUrl.searchParams.set('transactionId', transactionId || '');
      callbackUrl.searchParams.set('orderId', paymentDetails.orderId);
      window.location.href = callbackUrl.toString();
    } 
    // Fallback - just close or go back
    else {
      window.close();
      // If window.close() doesn't work (not opened as popup)
      window.history.back();
    }
  };

  // Check for payment callback params (when redirected back from PhonePe)
  useEffect(() => {
    const status = searchParams.get('status');
    const txnId = searchParams.get('transactionId');
    
    if (status) {
      handlePaymentCallback(status, txnId);
    }
  }, [searchParams]);

  if (loading) {
    return (
      <PaymentContainer>
        <PaymentCard>
          <PaymentBrandPanel gateway="PhonePe" eyebrow="Payment preparation" />
          <CheckoutPanel>
            <StatusContainer>
              <LoadingSpinner />
              <StatusTitle>Loading payment details</StatusTitle>
              <StatusMessage>Preparing a secure checkout for your order.</StatusMessage>
            </StatusContainer>
          </CheckoutPanel>
        </PaymentCard>
      </PaymentContainer>
    );
  }

  // Payment Status Screen
  if (paymentStatus === 'success' || paymentStatus === 'failed' || paymentStatus === 'pending') {
    return (
      <PaymentContainer>
        <PaymentCard>
          <PaymentBrandPanel gateway="PhonePe" eyebrow="Transaction status" />
          <CheckoutPanel>
            <StatusContainer>
              <StatusIcon className={paymentStatus}>
                {paymentStatus === 'success' && '✓'}
                {paymentStatus === 'failed' && '✗'}
                {paymentStatus === 'pending' && '⏳'}
              </StatusIcon>
              <StatusTitle>
                {paymentStatus === 'success' && 'Payment successful'}
                {paymentStatus === 'failed' && 'Payment failed'}
                {paymentStatus === 'pending' && 'Payment pending'}
              </StatusTitle>
              <StatusMessage>
                {paymentStatus === 'success' && 'Your payment has been processed successfully.'}
                {paymentStatus === 'failed' && 'Your payment could not be processed. Please try again.'}
                {paymentStatus === 'pending' && 'Your payment is being processed. Please wait.'}
              </StatusMessage>
              {transactionId && (
                <TransactionId>Transaction ID: {transactionId}</TransactionId>
              )}
              <CloseButton onClick={closePaymentWindow}>
                {paymentStatus === 'success' ? 'Done' : 'Close'}
              </CloseButton>
            </StatusContainer>
          </CheckoutPanel>
        </PaymentCard>
      </PaymentContainer>
    );
  }

  // Processing Screen
  if (paymentStatus === 'processing') {
    return (
      <PaymentContainer>
        <PaymentCard>
          <PaymentBrandPanel gateway="PhonePe" eyebrow="Gateway connection" />
          <CheckoutPanel>
            <StatusContainer>
              <LoadingSpinner />
              <StatusTitle>Processing payment</StatusTitle>
              <StatusMessage>Please wait while we redirect you to PhonePe.</StatusMessage>
            </StatusContainer>
          </CheckoutPanel>
        </PaymentCard>
      </PaymentContainer>
    );
  }

  // Initial Payment Screen
  return (
    <PaymentContainer>
      <PaymentCard>
        <PaymentBrandPanel gateway="PhonePe" />
        <CheckoutPanel>
          <Header>
            <span>Checkout / PhonePe</span>
            <Title>Complete payment</Title>
            <Subtitle>Review the details before continuing to the secure gateway.</Subtitle>
          </Header>

        {error && <ErrorMessage>{error}</ErrorMessage>}

        {paymentDetails && (
          <>
            <AmountDisplay>
              <AmountLabel>Amount to Pay</AmountLabel>
              <Amount>
                ₹{paymentDetails.amount.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </Amount>
            </AmountDisplay>

            <OrderInfo>
              <OrderRow>
                <OrderLabel>Order ID</OrderLabel>
                <OrderValue>{paymentDetails.orderId}</OrderValue>
              </OrderRow>
              <OrderRow>
                <OrderLabel>Description</OrderLabel>
                <OrderValue>{paymentDetails.description}</OrderValue>
              </OrderRow>
              <OrderRow>
                <OrderLabel>From</OrderLabel>
                <OrderValue>{paymentDetails.appName}</OrderValue>
              </OrderRow>
              {paymentDetails.customerName && (
                <OrderRow>
                  <OrderLabel>Customer</OrderLabel>
                  <OrderValue>{paymentDetails.customerName}</OrderValue>
                </OrderRow>
              )}
            </OrderInfo>

            <PayButton onClick={initiatePayment}>
              Pay with PhonePe
            </PayButton>

            <SecureMark>Your payment is secured with 256-bit encryption</SecureMark>
          </>
        )}
        </CheckoutPanel>
      </PaymentCard>
    </PaymentContainer>
  );
};

export default Payment;
